import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import user from "../../../assets/image/user.jpg";
import logo from "../../../assets/image/kkgt-header-image.jpg";
import { startTransition } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../../services/interceptor";
import Loading from "../../Loading";

import {
  FaEnvelope,
  FaPhone as FaPhoneIcon,
  FaHome,
  FaBirthdayCake,
  FaVenusMars,
  FaHeartbeat,
  FaTint,
  FaNotesMedical,
  FaTooth,
  FaSmoking,
  FaWineGlass,
  FaJoint,
  FaPills,
  FaIdCard,
  FaStethoscope,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

// Simplified animations without external dependencies
const AnimatedContainer = ({ children, className }) => (
  <div className={`transition-all duration-300 ease-in-out ${className}`}>
    {children}
  </div>
);

const OPDList = () => {
  const navigate = useNavigate();

  // State for API data
  const [opdData, setOpdData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Filter and sorting states
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // State to manage which tiles are expanded
  const [expandedTiles, setExpandedTiles] = useState({});

  const itemsPerPageOptions = [5, 10, 15, 20, 25, 50];

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Increased debounce time for API calls

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Reset to first page when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, dateFrom, dateTo, sortBy, sortOrder, itemsPerPage]);

  // Fetch OPD data from API
  const fetchOPDData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        sortBy,
        sortOrder,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
      });

      const response = await API.get(`/api/doctor/opd?${params}`);

      if (response.data.success) {
        setOpdData(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalRecords(response.data.pagination.totalRecords);
      } else {
        setError("Failed to fetch data");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching OPD data");
      console.error("Error fetching OPD data:", err);
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    sortBy,
    sortOrder,
    debouncedSearchTerm,
    dateFrom,
    dateTo,
  ]);

  // Load data when dependencies change
  useEffect(() => {
    fetchOPDData();
  }, [fetchOPDData]);

  const toggleTile = useCallback((id) => {
    setExpandedTiles((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  const handleCheckup = useCallback(
    (id) => {
      console.log(`Checkup for OPD ID: ${id}`);
      navigate(`/dashboard/checkup/${id}`);
    },
    [navigate]
  );

  const handleEdit = useCallback(
    (id) => {
      startTransition(() => {
        navigate(`/dashboard/edit-opd/${id}`);
      });
    },
    [navigate]
  );

  // Helper function to render dental history items safely
  const renderDentalHistory = useCallback((history) => {
    if (!history) return "N/A";

    if (typeof history === "string") return history;

    if (Array.isArray(history)) {
      if (history.length === 0) return "No history";

      return history
        .map((item, index) => {
          if (typeof item === "string") return item;
          if (item && typeof item === "object") {
            return `${item.reason || "No reason"} ${
              item.id ? `(ID: ${item.id})` : ""
            }`;
          }
          return `Item ${index + 1}`;
        })
        .join(", ");
    }

    if (typeof history === "object") {
      return `${history.reason || "No reason"} ${
        history.id ? `(ID: ${history.id})` : ""
      }`;
    }

    return String(history);
  }, []);

  // Handle deleting an OPD record
  const handleDelete = useCallback(
    async (id) => {
      const confirmDelete = window.confirm(
        "Are you sure you want to delete this OPD? This action cannot be undone."
      );
      if (!confirmDelete) return;

      toast.loading("Deleting OPD...", {
        position: "top-center",
        toastId: "delete-opd",
      });

      try {
        await API.delete(`/api/doctor/delete-opd-patientrecord/${id}`);

        toast.dismiss("delete-opd");
        toast.success("OPD deleted successfully!", {
          position: "top-center",
          autoClose: 3000,
        });

        // Refresh data after deletion
        fetchOPDData();
      } catch (err) {
        console.error("Error deleting OPD:", err);

        toast.dismiss("delete-opd");
        toast.error(err.response?.data?.message || "Failed to delete OPD.", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    },
    [fetchOPDData]
  );

  // Handle page change
  const handlePageChange = useCallback((pageNumber) => {
    setCurrentPage(pageNumber);
  }, []);

  // Handle items per page change
  const handleItemsPerPageChange = useCallback((e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  }, []);

  // Handle sort toggle
  const handleSort = useCallback(
    (field) => {
      if (sortBy === field) {
        setSortOrder(sortOrder === "asc" ? "desc" : "asc");
      } else {
        setSortBy(field);
        setSortOrder("desc");
      }
    },
    [sortBy, sortOrder]
  );

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  // Format date helper
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm("");
    setDateFrom("");
    setDateTo("");
    setSortBy("createdAt");
    setSortOrder("desc");
    setCurrentPage(1);
  };

  // Memoized Header Section
  const HeaderSection = useMemo(
    () => (
      <div className="text-center my-10 ">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">All Patients</h1>
        <p className="text-gray-600">
          Manage patient records and create appointments
        </p>
      </div>
    ),
    []
  );

  if (loading && opdData.length === 0) {
    return <Loading />;
  }

  return (
    <AnimatedContainer className="mx-auto p-6">
      {HeaderSection}

      {/* Search and Controls Section */}
      <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6 mb-6">
        {/* Quick Stats Summary */}
        {!loading && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">
                  {totalRecords}
                </div>
                <div className="text-sm text-gray-600">Total Records</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {totalPages}
                </div>
                <div className="text-sm text-gray-600">Total Pages</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {currentPage}
                </div>
                <div className="text-sm text-gray-600">Current Page</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-orange-600">
                  {opdData.length}
                </div>
                <div className="text-sm text-gray-600">Showing Now</div>
              </div>
            </div>
          </div>
        )}

        {/* Search and Sort Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Search Filter */}
          <div>
            <label htmlFor="search" className="block font-semibold mb-1">
              Search Records:
            </label>
            <div className="relative">
              <FaSearch className="absolute top-3 left-3 text-blue-400" />
              <input
                id="search"
                type="text"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, mobile, OPD number..."
              />
            </div>
          </div>

          {/* Date From */}
          <div>
            <label htmlFor="dateFrom" className="block font-semibold mb-1">
              From Date:
            </label>
            <input
              id="dateFrom"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Date To */}
          <div>
            <label htmlFor="dateTo" className="block font-semibold mb-1">
              To Date:
            </label>
            <input
              id="dateTo"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Items Per Page */}
          <div>
            <label htmlFor="itemsPerPage" className="block font-semibold mb-1">
              Items Per Page:
            </label>
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {itemsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Sort Controls and Clear Filters */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex gap-2">
            <button
              onClick={() => handleSort("createdAt")}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                sortBy === "createdAt"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Sort by Date{" "}
              {sortBy === "createdAt" && (sortOrder === "asc" ? "↑" : "↓")}
            </button>
            <button
              onClick={() => handleSort("opdNumber")}
              className={`px-4 py-2 rounded-lg border transition-colors ${
                sortBy === "opdNumber"
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
            >
              Sort by OPD No.{" "}
              {sortBy === "opdNumber" && (sortOrder === "asc" ? "↑" : "↓")}
            </button>
          </div>

          <button
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading records...</span>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className="text-red-600 font-medium">Error: {error}</div>
            <button
              onClick={fetchOPDData}
              className="ml-4 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Mobile View */}
      <div className="block md:hidden space-y-6">
        {!loading && opdData.length > 0 ? (
          opdData.map((opd, index) => (
            <div
              key={opd._id}
              className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6"
            >
              {/* Basic Info Table */}
              <div className="overflow-x-auto mb-4">
                <table className="w-full border-collapse border border-gray-300">
                  <tbody>
                    <tr className="bg-gray-100">
                      <td className="border px-4 py-2 font-semibold">
                        OPD Number
                      </td>
                      <td className="border px-4 py-2 text-blue-600 font-semibold">
                        {opd.opdNumber}
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">Name</td>
                      <td className="border px-4 py-2">
                        {opd.userDetails?.name || "N/A"}
                      </td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td className="border px-4 py-2 font-semibold">
                        Chief Complaint
                      </td>
                      <td className="border px-4 py-2">
                        {opd.checkupInfo || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td className="border px-4 py-2 font-semibold">Age</td>
                      <td className="border px-4 py-2">{opd.age || "N/A"}</td>
                    </tr>
                    <tr className="bg-gray-100">
                      <td className="border px-4 py-2 font-semibold">
                        Created At
                      </td>
                      <td className="border px-4 py-2">
                        {formatDate(opd.createdAt)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Toggle Details Button */}
              <div className="text-center mb-4">
                <button
                  onClick={() => toggleTile(opd._id)}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors font-bold"
                >
                  {expandedTiles[opd._id] ? "View Less" : "View More"}
                </button>
              </div>

              {/* Expanded Content */}
              {expandedTiles[opd._id] && (
                <div className="border-t pt-4 space-y-6">
                  {/* Patient Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">
                      Patient Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <tbody>
                          <tr className="bg-gray-100">
                            <td className="border px-4 py-2 font-medium">
                              Email
                            </td>
                            <td className="border px-4 py-2">
                              {opd.user?.email || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2 font-medium">
                              Contact
                            </td>
                            <td className="border px-4 py-2">
                              {opd.userDetails?.contact?.mobile || "N/A"}
                            </td>
                          </tr>
                          <tr className="bg-gray-100">
                            <td className="border px-4 py-2 font-medium">
                              Address
                            </td>
                            <td className="border px-4 py-2">
                              {opd.userDetails?.contact?.address || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2 font-medium">
                              Gender
                            </td>
                            <td className="border px-4 py-2">
                              {opd.userDetails?.gender || "N/A"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Medical Details */}
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-700">
                      Medical Details
                    </h3>
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <tbody>
                          <tr className="bg-gray-100">
                            <td className="border px-4 py-2 font-medium">
                              Blood Group
                            </td>
                            <td className="border px-4 py-2">
                              {opd.medicalDetails?.bloodGroup || "N/A"}
                            </td>
                          </tr>
                          <tr>
                            <td className="border px-4 py-2 font-medium">
                              Medical History
                            </td>
                            <td className="border px-4 py-2">
                              {Array.isArray(opd.medicalDetails?.medicalHistory)
                                ? opd.medicalDetails?.medicalHistory.join(", ")
                                : opd.medicalDetails?.medicalHistory || "N/A"}
                            </td>
                          </tr>
                          <tr className="bg-gray-100">
                            <td className="border px-4 py-2 font-medium">
                              Past Dental History
                            </td>
                            <td className="border px-4 py-2">
                              {renderDentalHistory(
                                opd.medicalDetails?.pastDentalHistory
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-center space-y-2 sm:space-y-0 sm:space-x-4 pt-4">
                    <button
                      className="bg-blue-500 text-white px-6 py-2 rounded-lg hover:bg-blue-600 focus:outline-none font-bold"
                      onClick={() => handleEdit(opd._id)}
                    >
                      Edit Case
                    </button>
                    <button
                      className="bg-red-500 text-white px-6 py-2 rounded-lg hover:bg-red-600 focus:outline-none font-bold"
                      onClick={() => handleDelete(opd._id)}
                    >
                      Delete Case
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))
        ) : !loading ? (
          <div className="text-center text-gray-600 py-8">
            No OPD records found matching your search criteria.
          </div>
        ) : null}
      </div>

      {/* Desktop View */}
      <div className="shadow-md rounded-lg bg-gradient-to-r from-white to-white p-6 hidden md:block">
        {!loading && opdData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 rounded-lg shadow-md">
              <thead>
                <tr className="bg-blue-600 text-white text-center border border-gray-300">
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    OPD Number
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    Name
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    Chief Complaint
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    Age
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    Created At
                  </th>
                  <th className="px-4 py-3 border border-gray-300 font-semibold">
                    More Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {opdData.map((opd) => (
                  <React.Fragment key={opd._id}>
                    <tr className="bg-white text-gray-800 border-b hover:bg-gray-100 transition-all text-center">
                      <td className="px-4 py-3 font-semibold text-blue-600 border border-gray-300">
                        {opd.opdNumber}
                      </td>
                      <td className="px-4 py-3 text-gray-700 border border-gray-300">
                        {opd.userDetails?.name || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-gray-700 border border-gray-300">
                        {opd.checkupInfo || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 border border-gray-300">
                        {opd.age || "N/A"}
                      </td>
                      <td className="px-4 py-3 text-gray-600 border border-gray-300">
                        {formatDate(opd.createdAt)}
                      </td>
                      <td className="px-4 py-3 border border-gray-300">
                        <button
                          onClick={() => toggleTile(opd._id)}
                          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 shadow-md hover:shadow-lg transition-all font-bold"
                        >
                          {expandedTiles[opd._id] ? "View Less" : "View More"}
                        </button>
                      </td>
                    </tr>

                    {/* Expanded Details */}
                    {expandedTiles[opd._id] && (
                      <tr>
                        <td
                          colSpan="6"
                          className="px-4 pb-4 bg-gray-100 border border-gray-300"
                        >
                          <div className="p-6">
                            <h2 className="text-3xl font-bold text-blue-700 mb-6 flex items-center gap-2">
                              <ClipboardList
                                size={28}
                                className="text-blue-600"
                              />
                              OPD Case Details
                            </h2>

                            {/* Grid Layout */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                              {/* Patient Information Card */}
                              <div className="p-6 border rounded-xl bg-blue-50 shadow-lg hover:shadow-xl transition-all border-blue-300 h-64 flex flex-col">
                                <h3 className="text-xl font-semibold text-blue-700 flex items-center gap-2 mb-3">
                                  <FaIdCard
                                    size={20}
                                    className="text-blue-500"
                                  />
                                  Patient Details
                                </h3>
                                <div className="overflow-y-auto flex-grow">
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaEnvelope
                                      size={16}
                                      className="text-blue-500 flex-shrink-0"
                                    />
                                    <strong>Email:</strong>{" "}
                                    {opd.user?.email || "N/A"}
                                  </p>
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaPhoneIcon
                                      size={16}
                                      className="text-blue-500 flex-shrink-0"
                                    />
                                    <strong>Contact:</strong>{" "}
                                    {opd.userDetails?.contact?.mobile || "N/A"}
                                  </p>
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaHome
                                      size={16}
                                      className="text-blue-500 flex-shrink-0"
                                    />
                                    <strong>Address:</strong>{" "}
                                    {opd.userDetails?.contact?.address || "N/A"}
                                  </p>
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaBirthdayCake
                                      size={16}
                                      className="text-blue-500 flex-shrink-0"
                                    />
                                    <strong>DOB:</strong>{" "}
                                    {opd.userDetails?.dateOfBirth
                                      ? formatDate(opd.userDetails.dateOfBirth)
                                      : "N/A"}
                                  </p>
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaVenusMars
                                      size={16}
                                      className="text-blue-500 flex-shrink-0"
                                    />
                                    <strong>Gender:</strong>{" "}
                                    {opd.userDetails?.gender || "N/A"}
                                  </p>
                                </div>
                              </div>

                              {/* Medical Information Card */}
                              <div className="p-6 border rounded-xl bg-green-50 shadow-lg hover:shadow-xl transition-all border-green-300 h-64 flex flex-col">
                                <h3 className="text-xl font-semibold text-green-700 flex items-center gap-2 mb-3">
                                  <FaHeartbeat
                                    size={20}
                                    className="text-green-500"
                                  />
                                  Medical Details
                                </h3>
                                <div className="overflow-y-auto flex-grow">
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaTint
                                      size={16}
                                      className="text-green-500 flex-shrink-0"
                                    />
                                    <strong>Blood Group:</strong>{" "}
                                    {opd.medicalDetails?.bloodGroup || "N/A"}
                                  </p>
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaNotesMedical
                                      size={16}
                                      className="text-green-500 flex-shrink-0"
                                    />
                                    <strong>Medical History:</strong>{" "}
                                    {Array.isArray(
                                      opd.medicalDetails?.medicalHistory
                                    )
                                      ? opd.medicalDetails?.medicalHistory.join(
                                          ", "
                                        )
                                      : opd.medicalDetails?.medicalHistory ||
                                        "N/A"}
                                  </p>
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaTooth
                                      size={16}
                                      className="text-green-500 flex-shrink-0"
                                    />
                                    <strong>Past Dental History:</strong>{" "}
                                    {renderDentalHistory(
                                      opd.medicalDetails?.pastDentalHistory
                                    )}
                                  </p>
                                </div>
                              </div>

                              {/* Lifestyle & Habits Card */}
                              <div className="p-6 border rounded-xl bg-purple-50 shadow-lg hover:shadow-xl transition-all border-purple-300 h-64 flex flex-col">
                                <h3 className="text-xl font-semibold text-purple-700 flex items-center gap-2 mb-3">
                                  <FaPills
                                    size={20}
                                    className="text-purple-500"
                                  />
                                  Lifestyle & Habits
                                </h3>
                                <div className="overflow-y-auto flex-grow">
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaSmoking
                                      size={16}
                                      className="text-purple-500 flex-shrink-0"
                                    />
                                    <strong>Smoking:</strong>{" "}
                                    {opd.medicalDetails?.lifestyleAndHabits
                                      ?.smokingStatus || "N/A"}
                                  </p>
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaWineGlass
                                      size={16}
                                      className="text-purple-500 flex-shrink-0"
                                    />
                                    <strong>Alcohol:</strong>{" "}
                                    {opd.medicalDetails?.lifestyleAndHabits
                                      ?.alcoholUse || "N/A"}
                                  </p>
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaJoint
                                      size={16}
                                      className="text-purple-500 flex-shrink-0"
                                    />
                                    <strong>Tobacco:</strong>{" "}
                                    {opd.medicalDetails?.lifestyleAndHabits
                                      ?.tobacco || "N/A"}
                                  </p>
                                </div>
                              </div>

                              {/* Additional Info Card */}
                              <div className="p-6 border rounded-xl bg-amber-50 shadow-lg hover:shadow-xl transition-all border-amber-300 h-64 flex flex-col">
                                <h3 className="text-xl font-semibold text-amber-700 flex items-center gap-2 mb-3">
                                  <FaStethoscope
                                    size={20}
                                    className="text-amber-500"
                                  />
                                  Additional Information
                                </h3>
                                <div className="overflow-y-auto flex-grow">
                                  <p className="mb-2 flex items-center gap-2">
                                    <FaNotesMedical
                                      size={16}
                                      className="text-amber-500 flex-shrink-0"
                                    />
                                    <strong>Notes:</strong>{" "}
                                    {opd.medicalDetails?.notes ||
                                      "No notes available"}
                                  </p>
                                </div>
                              </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex justify-center space-x-4 mt-8">
                              <button
                                className="bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 shadow-lg hover:shadow-xl transition-all font-bold"
                                onClick={() => handleEdit(opd._id)}
                              >
                                Edit Case
                              </button>
                              <button
                                className="bg-red-500 text-white px-6 py-3 rounded-lg hover:bg-red-600 shadow-lg hover:shadow-xl transition-all font-bold"
                                onClick={() => handleDelete(opd._id)}
                              >
                                Delete Case
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : !loading ? (
          <div className="text-center text-gray-600 py-8">
            No OPD records found matching your search criteria.
          </div>
        ) : null}
      </div>

      {/* Enhanced Pagination Controls */}
      {totalPages > 1 && !loading && (
        <div className="mt-8 bg-white rounded-lg shadow-sm border p-6">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            {/* Pagination Info */}
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalRecords)} of{" "}
              {totalRecords} results
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center gap-2">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <FaChevronLeft size={12} />
                Previous
              </button>

              {/* First Page */}
              {currentPage > 3 && (
                <>
                  <button
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    1
                  </button>
                  {currentPage > 4 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                </>
              )}

              {/* Page Numbers */}
              {getPageNumbers().map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage === pageNum
                      ? "bg-blue-600 text-white border border-blue-600"
                      : "text-gray-500 bg-white border border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {pageNum}
                </button>
              ))}

              {/* Last Page */}
              {currentPage < totalPages - 2 && (
                <>
                  {currentPage < totalPages - 3 && (
                    <span className="px-2 text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    {totalPages}
                  </button>
                </>
              )}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Next
                <FaChevronRight size={12} />
              </button>
            </div>

            {/* Items Per Page Selector */}
            <div className="flex items-center gap-2">
              <label
                htmlFor="bottomItemsPerPage"
                className="text-sm font-medium text-gray-700"
              >
                Items per page:
              </label>
              <select
                id="bottomItemsPerPage"
                value={itemsPerPage}
                onChange={handleItemsPerPageChange}
                className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {itemsPerPageOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </AnimatedContainer>
  );
};

export default OPDList;
